/* eslint-disable no-underscore-dangle */
import React, { useState, useContext } from 'react';
import { Link, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import {
	AppBar,
	Button,
	IconButton,
	Toolbar,
	Hidden,
	colors,
	Select,
	MenuItem,
	InputBase
} from '@material-ui/core';
import InputIcon from '@material-ui/icons/Input';
import MenuIcon from '@material-ui/icons/Menu';
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom';
import PostAddIcon from '@material-ui/icons/PostAdd';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import SyncIcon from '@material-ui/icons/Sync';
import Authentication from 'src/utils/Authentication';
import WalkInModal from 'src/pages/WalkIn/WalkInModal';
import { ContextProvider } from 'src/utils/AzureProvider';
import { changeSelectedProjectApi } from 'src/thunks';
import AddNoteModal from 'src/pages/Contacts/details/AddNoteModal';
import AddContactModal from 'src/pages/Contacts/AddContactModal';
import SearchBar from 'src/components/SearchBar';
import SyncLeadsResultsDialog from 'src/utils/SyncLeadsResultsDialog';
import { syncContactLeadsApi } from 'src/api/contactApi';
import { getProject, getSelectedProject, getAuth } from '../../selectors';
import { changeSelectedProject, resetUserStore, setChangeProject } from '../../actions';
import { isAdmin } from '../../utils';

const useStyles = makeStyles((theme) => ({
	root: {
		boxShadow: '0 1px 3px rgba(0,0,0,0.18)'
	},
	flexGrow: {
		flexGrow: 1
	},
	search: {
		backgroundColor: 'rgba(255,255,255, 0.1)',
		borderRadius: 4,
		flexBasis: 300,
		height: 36,
		padding: theme.spacing(0, 2),
		display: 'flex',
		alignItems: 'center'
	},
	searchIcon: {
		marginRight: theme.spacing(2),
		color: 'inherit'
	},
	searchInput: {
		flexGrow: 1,
		color: 'inherit',
		'& input::placeholder': {
			opacity: 1,
			color: 'inherit'
		}
	},
	headerLogo: {
		color: '#FFF',
		fontSize: '24px',
		fontFamily: 'Roboto, Helvetica, Arial, sansSerif',
		fontWeight: 500,
		height: 40,
		display: 'flex',
		alignItems: 'center'
	},
	img: {
		height: '100%'
	},
	searchPopper: {
		zIndex: theme.zIndex.appBar + 100
	},
	searchPopperContent: {
		marginTop: theme.spacing(1)
	},
	menuButton: {
		marginRight: theme.spacing(1)
	},
	chatButton: {
		marginLeft: theme.spacing(1)
	},
	notificationsButton: {
		marginLeft: theme.spacing(1)
	},
	notificationsBadge: {
		backgroundColor: colors.orange[600]
	},
	projectInput: {
		marginLeft: 16,
		display: 'inline-flex',
		alignItems: 'center',
		height: 32,
		backgroundColor: '#fff',
		borderRadius: 6,
		minWidth: 180,
		maxWidth: 260,
		overflow: 'hidden',
		boxShadow: '0 1px 2px rgba(0,0,0,0.12)'
	},
	selectRoot: {
		width: '100%',
		height: '100%'
	},
	selectInputBase: {
		width: '100%',
		height: '100%',
		fontSize: 13,
		fontWeight: 500,
		color: '#1a1a1a',
		'& .MuiSelect-select': {
			display: 'flex',
			alignItems: 'center',
			height: '100%',
			boxSizing: 'border-box',
			padding: '0 28px 0 12px !important',
			backgroundColor: 'transparent !important',
			lineHeight: '32px',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap'
		},
		'& .MuiSelect-select:focus': {
			backgroundColor: 'transparent'
		},
		'& .MuiSelect-icon': {
			color: '#757575',
			right: 6,
			top: 'calc(50% - 12px)'
		}
	},
	projectMenuPaper: {
		backgroundColor: '#fff !important',
		color: '#222',
		maxHeight: 360,
		minWidth: '220px !important',
		maxWidth: 320,
		boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
		borderRadius: 6,
		marginTop: 4,
		border: '1px solid #e8e8e8',
		overflowX: 'hidden',
		overflowY: 'auto',
		scrollbarWidth: 'thin',
		scrollbarColor: 'rgba(0,0,0,0.22) transparent',
		'&::-webkit-scrollbar': {
			width: 6,
			height: 6
		},
		'&::-webkit-scrollbar-track': {
			background: 'transparent'
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: 'rgba(0,0,0,0.22)',
			borderRadius: 6,
			border: '1px solid transparent',
			backgroundClip: 'padding-box',
			'&:hover': {
				backgroundColor: 'rgba(0,0,0,0.35)'
			}
		}
	},
	projectMenuList: {
		paddingTop: 4,
		paddingBottom: 4,
		scrollbarWidth: 'thin',
		scrollbarColor: 'rgba(0,0,0,0.22) transparent',
		'&::-webkit-scrollbar': {
			width: 6,
			height: 6
		},
		'&::-webkit-scrollbar-track': {
			background: 'transparent'
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: 'rgba(0,0,0,0.22)',
			borderRadius: 6,
			border: '1px solid transparent',
			backgroundClip: 'padding-box',
			'&:hover': {
				backgroundColor: 'rgba(0,0,0,0.35)'
			}
		}
	},
	projectMenuItem: {
		fontSize: 13,
		fontWeight: 400,
		color: '#333 !important',
		minHeight: 36,
		paddingTop: 8,
		paddingBottom: 8,
		paddingLeft: 12,
		paddingRight: 12,
		'&:hover': {
			backgroundColor: '#f5f5f5'
		},
		'&.Mui-selected': {
			backgroundColor: '#eee !important',
			fontWeight: 500,
			color: '#111 !important',
			'&:hover': {
				backgroundColor: '#e8e8e8 !important'
			}
		}
	},
	projectIcon: {
		fontSize: '30px',
		cursor: 'pointer',
		alignSelf: 'center',
		marginLeft: '15px',
		padding: '12px 15px'
	},
	headerBar: {
		background: '#111',
		minHeight: 56
	},
	toolbar: {
		minHeight: 56,
		height: 56,
		paddingLeft: 16,
		paddingRight: 16,
		paddingTop: 0,
		paddingBottom: 0,
		alignItems: 'center'
	},
	headerActions: {
		display: 'inline-flex',
		alignItems: 'stretch',
		height: 56,
		marginLeft: 4,
		marginRight: 0
	},
	walkInButton: {
		color: 'rgba(255,255,255,0.78)',
		fontSize: 12,
		fontWeight: 400,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		textTransform: 'none',
		borderRadius: 0,
		padding: '0 16px',
		margin: 0,
		minWidth: 0,
		minHeight: 56,
		height: 56,
		lineHeight: 1.2,
		'& .MuiSvgIcon-root': {
			fontSize: 17,
			marginRight: 6,
			opacity: 0.85
		},
		'&:hover': {
			backgroundColor: 'rgba(255,255,255,0.1)',
			color: '#fff'
		}
	},
	logoutButton: {
		marginLeft: theme.spacing(0.5),
		textTransform: 'none',
		fontSize: 13,
		padding: '0 16px',
		borderRadius: 0,
		minHeight: 56,
		height: 56,
		color: 'rgba(255,255,255,0.85)',
		'&:hover': {
			backgroundColor: 'rgba(255,255,255,0.1)',
			color: '#fff'
		}
	},
	logoutIcon: {
		marginRight: theme.spacing(0.75),
		fontSize: 18
	}
}));

function TopBar({ onOpenNavBarMobile, className, ...rest }) {
	const classes = useStyles();
	const history = useHistory();
	const dispatch = useDispatch();
	const [open, setOpen] = useState(false);
	const [openNote, setNoteOpen] = useState(false);
	const [addContact, setAddContact] = useState(false);
	const [syncDialog, setSyncDialog] = useState(false);
	const [syncLoading, setSyncLoading] = useState(false);
	const [syncError, setSyncError] = useState(null);
	const [syncResults, setSyncResults] = useState(null);
	const projects = useSelector(getProject);
	const currentProject = useSelector(getSelectedProject);
	const user = useSelector(getAuth);
	const userProjects = _.sortBy(
		isAdmin()
			? projects
			: _.filter(projects, (x) => _.find(user.projects, (i) => x.id === i)),
		(project) => (project?.name || '').toLowerCase()
	);
	const azureProvider = useContext(ContextProvider);

	const handleLogout = () => {
		Authentication.logout();
		dispatch(resetUserStore());
		if (localStorage.getItem('accessMicrosoftToken')) {
			azureProvider.logout();
			localStorage.removeItem('accessMicrosoftToken');
		}
		history.push('/admin/auth/login');
	};

	const redirectToPage = () => {
		if (/\/admin\/sales-details\/\S{5}/.test(window.location.pathname)) {
			history.push('/admin/sales');
		} else {
			const pathArr = window.location.pathname.split('/');
			if (pathArr.length > 3) {
				history.push(`/admin/${pathArr[2]}`);
			}
		}
	};

	const handleProjectChange = async (e) => {
		dispatch(changeSelectedProjectApi({ userId: user.id, projectId: e.target.value }));
		dispatch(changeSelectedProject(e.target.value));
		dispatch(setChangeProject(true));
		redirectToPage();
	};

	const handleOpenSyncDialog = () => {
		setSyncDialog(true);
		setSyncLoading(false);
		setSyncError(null);
		setSyncResults(null);
	};

	const handleSyncLeads = async (selectedDate) => {
		setSyncLoading(true);
		setSyncError(null);
		setSyncResults(null);

		try {
			let sinceDate;
			if (selectedDate) {
				const date = new Date(selectedDate);
				date.setHours(0, 0, 0, 0);
				sinceDate = date.toISOString();
			}
			const response = await syncContactLeadsApi(sinceDate);
			setSyncResults(response.data);
		} catch (error) {
			setSyncError(error.response?.data?.message || 'Failed to sync leads. Please try again.');
		} finally {
			setSyncLoading(false);
		}
	};

	const handleCloseSyncDialog = () => {
		setSyncDialog(false);
		setSyncLoading(false);
		setSyncError(null);
		setSyncResults(null);
	};

	return (
		<AppBar
			{...rest}
			className={clsx(classes.root, className)}
			color="primary"
		>
			<Toolbar className={clsx(classes.headerBar, classes.toolbar)}>
				<Hidden lgUp>
					<IconButton
						className={classes.menuButton}
						color="inherit"
						onClick={onOpenNavBarMobile}
					>
						<MenuIcon />
					</IconButton>
				</Hidden>
				<Link to="/">
					<div className={classes.headerLogo}>
						<img
							className={classes.img}
							src="/images/logos/intrahub logo-05.png"
							alt="Logo"
						/>
					</div>
				</Link>
				{userProjects?.length > 1 && (
					<div className={classes.projectInput}>
						<Select
							name="project"
							className={classes.selectRoot}
							onChange={(e) => handleProjectChange(e)}
							value={currentProject || ''}
							displayEmpty
							autoWidth={false}
							input={
								<InputBase
									classes={{
										root: classes.selectInputBase
									}}
								/>
							}
							MenuProps={{
								getContentAnchorEl: null,
								anchorOrigin: {
									vertical: 'bottom',
									horizontal: 'left'
								},
								transformOrigin: {
									vertical: 'top',
									horizontal: 'left'
								},
								PaperProps: {
									className: classes.projectMenuPaper
								},
								MenuListProps: {
									className: classes.projectMenuList,
									dense: false
								},
								disableScrollLock: true,
								style: { zIndex: 1500 }
							}}
						>
							{userProjects?.map((option, idx) => (
								<MenuItem
									key={`${option.name}-${idx}`}
									value={option.id}
									className={classes.projectMenuItem}
								>
									{option.name}
								</MenuItem>
							))}
						</Select>
					</div>
				)}
				<Hidden smDown>
					<div className={classes.headerActions}>
						<Button
							className={classes.walkInButton}
							onClick={() => setOpen(true)}
						>
							<MeetingRoomIcon />
							Walk In
						</Button>
						<Button
							className={classes.walkInButton}
							onClick={() => setNoteOpen(true)}
						>
							<PostAddIcon />
							Contact Note
						</Button>
						<Button
							className={classes.walkInButton}
							onClick={() => setAddContact(true)}
						>
							<GroupAddIcon />
							Contact
						</Button>
						<Button
							className={classes.walkInButton}
							onClick={handleOpenSyncDialog}
							title="Manual Sync REA Leads"
						>
							<SyncIcon />
							Sync Leads
						</Button>
					</div>

					<WalkInModal
						onClose={() => setOpen(false)}
						open={open}
					/>
					<AddNoteModal
						onClose={() => setNoteOpen(false)}
						open={openNote}
						topBar
					/>
					<AddContactModal
						onClose={() => setAddContact(false)}
						open={addContact}
					/>
					<SyncLeadsResultsDialog
						open={syncDialog}
						onClose={handleCloseSyncDialog}
						onSync={handleSyncLeads}
						loading={syncLoading}
						error={syncError}
						syncResults={syncResults}
					/>
				</Hidden>
				<div className={classes.flexGrow} />
				<Hidden smDown>
					<SearchBar />
					<Button
						className={classes.logoutButton}
						color="inherit"
						onClick={handleLogout}
					>
						<InputIcon className={classes.logoutIcon} />
						Sign out
					</Button>
				</Hidden>
			</Toolbar>
		</AppBar>
	);
}

TopBar.propTypes = {
	className: PropTypes.string,
	onOpenNavBarMobile: PropTypes.func
};

export default TopBar;
